import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import './UserManagement.css'; // Reuse existing styles

// Get API base URL from environment or default
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const InvitationManagement = ({ token }) => {
  const [activeTab, setActiveTab] = useState('individual');
  const [serviceGroups, setServiceGroups] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Invitation groups
  const [invitationGroups, setInvitationGroups] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    type: [],
    status: [],
    serviceGroupId: '',
    invitationGroupId: '',
    searchTerm: '',
    createdFrom: '',
    createdTo: '',
    expiresFrom: '',
    expiresTo: '',
    hasOnboardingFlow: false,
    autoActivate: null,
    usageStatus: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [detailsModalData, setDetailsModalData] = useState(null);
  const [editModalData, setEditModalData] = useState(null);
  const [groupModalData, setGroupModalData] = useState(null);
  const [qrModalData, setQRModalData] = useState(null);
  const qrCodeRef = useRef(null);

  // Onboarding flows and assignment rules
  const [onboardingFlows, setOnboardingFlows] = useState([]);
  const [assignmentRules, setAssignmentRules] = useState([]);

  // Individual invitation form
  const [individualForm, setIndividualForm] = useState({
    invitedName: '',
    invitedEmail: '',
    serviceGroupId: '',
    onboardingFlowId: '',
    assignmentRuleId: '',
    sendEmail: true,
    showServiceGroupLogo: false
  });

  // Bulk upload state
  const [csvFile, setCsvFile] = useState(null);
  const [bulkValidation, setBulkValidation] = useState(null);
  const [bulkServiceGroupId, setBulkServiceGroupId] = useState('');
  const [bulkOnboardingFlowId, setBulkOnboardingFlowId] = useState('');
  const [bulkAssignmentRuleId, setBulkAssignmentRuleId] = useState('');
  const [bulkGroupOption, setBulkGroupOption] = useState('none'); // none, new, existing
  const [bulkNewGroupName, setBulkNewGroupName] = useState('');
  const [bulkNewGroupDescription, setBulkNewGroupDescription] = useState('');
  const [bulkExistingGroupId, setBulkExistingGroupId] = useState('');
  const [bulkSendEmails, setBulkSendEmails] = useState(true);

  // General link form
  const [generalLinkForm, setGeneralLinkForm] = useState({
    tag: '',
    description: '',
    serviceGroupId: '',
    onboardingFlowId: '',
    assignmentRuleId: '',
    maxUses: '',
    autoActivate: false,
    welcomeMessage: '',
    showServiceGroupLogo: false
  });

  useEffect(() => {
    fetchServiceGroups();
    fetchOnboardingFlows();
    fetchAssignmentRules();
    fetchInvitationGroups();
  }, []);

  useEffect(() => {
    if (activeTab === 'list' || activeTab === 'groups') {
      fetchInvitations();
    }
  }, [activeTab, pagination.page, pagination.limit, sortBy, sortOrder, filters]);

  const fetchServiceGroups = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/service-groups`, {
        headers: { 'x-auth-token': token }
      });
      const groups = res.data.data || []; // Response format is { success, count, data }
      setServiceGroups(groups);

      // Set first service group as default
      if (groups.length > 0) {
        setIndividualForm(prev => ({ ...prev, serviceGroupId: groups[0]._id }));
        setBulkServiceGroupId(groups[0]._id);
        setGeneralLinkForm(prev => ({ ...prev, serviceGroupId: groups[0]._id }));
      }
    } catch (err) {
      console.error('Error fetching service groups:', err);
      setMessage('Failed to load service groups');
    }
  };

  const fetchOnboardingFlows = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/onboarding-flows`, {
        headers: { 'x-auth-token': token }
      });
      setOnboardingFlows(res.data.flows || []);
    } catch (err) {
      console.error('Error fetching onboarding flows:', err);
      // Non-critical - system will use default if no flows available
    }
  };

  const fetchAssignmentRules = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/assignment-rules`, {
        headers: { 'x-auth-token': token }
      });
      setAssignmentRules(res.data.rules || []);
    } catch (err) {
      console.error('Error fetching assignment rules:', err);
      // Non-critical - system will use default if no rules available
    }
  };

  const fetchInvitationGroups = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/invitation-groups`, {
        headers: { 'x-auth-token': token }
      });
      setInvitationGroups(res.data.groups || []);
    } catch (err) {
      console.error('Error fetching invitation groups:', err);
    }
  };

  const fetchInvitations = async () => {
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder,
        ...buildFilterQuery()
      };

      // Remove empty/null parameters
      Object.keys(params).forEach(key =>
        (params[key] === null || params[key] === '' || params[key] === undefined ||
        (Array.isArray(params[key]) && params[key].length === 0)) && delete params[key]
      );

      const res = await axios.get(`${API_BASE_URL}/invitations`, {
        params,
        headers: { 'x-auth-token': token }
      });
      setInvitations(res.data.invitations || []);
      setPagination(res.data.pagination || pagination);
    } catch (err) {
      console.error('Error fetching invitations:', err);
    }
  };

  const buildFilterQuery = () => {
    const query = {};
    if (filters.type && filters.type.length > 0) query.type = filters.type.join(',');
    if (filters.status && filters.status.length > 0) query.status = filters.status.join(',');
    if (filters.serviceGroupId) query.serviceGroupId = filters.serviceGroupId;
    if (filters.invitationGroupId) query.invitationGroupId = filters.invitationGroupId;
    if (filters.searchTerm) query.searchTerm = filters.searchTerm;
    if (filters.createdFrom) query.createdFrom = filters.createdFrom;
    if (filters.createdTo) query.createdTo = filters.createdTo;
    if (filters.expiresFrom) query.expiresFrom = filters.expiresFrom;
    if (filters.expiresTo) query.expiresTo = filters.expiresTo;
    if (filters.hasOnboardingFlow) query.hasOnboardingFlow = filters.hasOnboardingFlow;
    if (filters.autoActivate !== null) query.autoActivate = filters.autoActivate;
    if (filters.usageStatus) query.usageStatus = filters.usageStatus;
    return query;
  };

  // Individual Invitation
  const handleIndividualSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const res = await axios.post(`${API_BASE_URL}/invitations/individual`, individualForm, {
        headers: { 'x-auth-token': token }
      });

      setMessage(`✓ Invitation created! ${individualForm.sendEmail ? 'Email sent to ' + individualForm.invitedEmail : 'Copy link: ' + res.data.invitation.invitationUrl}`);
      setIndividualForm({
        invitedName: '',
        invitedEmail: '',
        serviceGroupId: individualForm.serviceGroupId,
        onboardingFlowId: individualForm.onboardingFlowId,
        assignmentRuleId: individualForm.assignmentRuleId,
        sendEmail: true,
        showServiceGroupLogo: false
      });
      fetchInvitations();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to create invitation';
      setMessage('✗ ' + errorMsg);

      if (err.response?.data?.conflictType === 'existing_user') {
        setMessage(`✗ ${errorMsg}. This email is already registered.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Bulk CSV Upload
  const handleCSVValidate = async () => {
    if (!csvFile) {
      setMessage('✗ Please select a CSV file');
      return;
    }

    setLoading(true);
    setMessage('');

    const formData = new FormData();
    formData.append('csvFile', csvFile);
    formData.append('defaultServiceGroupId', bulkServiceGroupId);
    if (bulkOnboardingFlowId) {
      formData.append('onboardingFlowId', bulkOnboardingFlowId);
    }
    if (bulkAssignmentRuleId) {
      formData.append('assignmentRuleId', bulkAssignmentRuleId);
    }

    try {
      const res = await axios.post(`${API_BASE_URL}/invitations/bulk/validate`, formData, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'multipart/form-data'
        }
      });

      setBulkValidation(res.data.validation);
      setMessage(`✓ Validation complete: ${res.data.validation.summary.valid} valid, ${res.data.validation.summary.duplicate} duplicates, ${res.data.validation.summary.error} errors`);
    } catch (err) {
      setMessage('✗ Failed to validate CSV: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    if (!csvFile) {
      setMessage('✗ Please select a CSV file');
      return;
    }

    setLoading(true);
    setMessage('Creating invitations...');

    const formData = new FormData();
    formData.append('csvFile', csvFile);
    formData.append('defaultServiceGroupId', bulkServiceGroupId);
    formData.append('sendEmails', bulkSendEmails);
    if (bulkOnboardingFlowId) {
      formData.append('onboardingFlowId', bulkOnboardingFlowId);
    }
    if (bulkAssignmentRuleId) {
      formData.append('assignmentRuleId', bulkAssignmentRuleId);
    }

    // Handle invitation group options
    if (bulkGroupOption === 'new') {
      if (!bulkNewGroupName) {
        setMessage('✗ Please provide a group name');
        setLoading(false);
        return;
      }
      formData.append('createGroup', 'true');
      formData.append('groupName', bulkNewGroupName);
      formData.append('groupDescription', bulkNewGroupDescription);
    } else if (bulkGroupOption === 'existing') {
      if (!bulkExistingGroupId) {
        setMessage('✗ Please select an existing group');
        setLoading(false);
        return;
      }
      formData.append('groupId', bulkExistingGroupId);
    }

    try {
      const res = await axios.post(`${API_BASE_URL}/invitations/bulk/create`, formData, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'multipart/form-data'
        }
      });

      let successMessage = `✓ Created ${res.data.summary.invitationsCreated} invitations.`;
      if (res.data.summary.emailsSent > 0) {
        successMessage += ` ${res.data.summary.emailsSent} emails sent.`;
      }
      if (res.data.invitationGroup) {
        successMessage += ` Group: "${res.data.invitationGroup.name}"`;
      }
      if (res.data.summary.duplicateRows > 0) {
        successMessage += ` Duplicates skipped: ${res.data.summary.duplicateRows}`;
      }

      setMessage(successMessage);
      setCsvFile(null);
      setBulkValidation(null);
      setBulkGroupOption('none');
      setBulkNewGroupName('');
      setBulkNewGroupDescription('');
      setBulkExistingGroupId('');
      fetchInvitations();
      fetchInvitationGroups();
    } catch (err) {
      setMessage('✗ Failed to create invitations: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // General Link
  const handleGeneralLinkSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const res = await axios.post(`${API_BASE_URL}/invitations/general-link`, {
        ...generalLinkForm,
        maxUses: generalLinkForm.maxUses ? parseInt(generalLinkForm.maxUses) : null
      }, {
        headers: { 'x-auth-token': token }
      });

      setMessage(`✓ General link created! Code: ${res.data.invitation.signupCode} | URL: ${res.data.invitation.signupUrl}`);
      setGeneralLinkForm({
        tag: '',
        description: '',
        serviceGroupId: generalLinkForm.serviceGroupId,
        onboardingFlowId: generalLinkForm.onboardingFlowId,
        assignmentRuleId: generalLinkForm.assignmentRuleId,
        maxUses: '',
        autoActivate: false,
        welcomeMessage: '',
        showServiceGroupLogo: false
      });
      fetchInvitations();
    } catch (err) {
      setMessage('✗ ' + (err.response?.data?.message || 'Failed to create general link'));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/invitations/bulk/template`, {
        headers: { 'x-auth-token': token },
        responseType: 'blob' // Important for file download
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'invitation-template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage('✓ CSV template downloaded!');
    } catch (err) {
      setMessage('Failed to download template: ' + (err.response?.data?.msg || err.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage('✓ Copied to clipboard!');
    setTimeout(() => setMessage(''), 2000);
  };

  // QR Code handlers
  const handleShowQR = (inv) => {
    // Use path-based URL for better iOS Camera QR code support
    // iOS Camera sometimes strips query parameters, so /signup/:code is more reliable
    const url = `${window.location.origin}/signup/${inv.signupCode}`;
    setQRModalData({
      url,
      signupCode: inv.signupCode,
      tag: inv.generalLinkSettings?.tag || inv.signupCode
    });
    setShowQRModal(true);
  };

  const downloadQRCode = (format = 'png') => {
    if (!qrCodeRef.current) return;

    const svg = qrCodeRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Use higher resolution for better quality
      const scale = 4;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      // White background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const link = document.createElement('a');
      const filename = `qr-code-${qrModalData.tag || qrModalData.signupCode}`;

      if (format === 'svg') {
        const blob = new Blob([svgData], { type: 'image/svg+xml' });
        link.href = URL.createObjectURL(blob);
        link.download = `${filename}.svg`;
      } else {
        link.href = canvas.toDataURL('image/png');
        link.download = `${filename}.png`;
      }

      link.click();
      setMessage(`✓ QR code downloaded as ${format.toUpperCase()}`);
      setTimeout(() => setMessage(''), 2000);
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // New invitation management handlers
  const handleDeleteInvitation = async (id, force = false) => {
    if (!window.confirm('Are you sure you want to delete this invitation?')) return;

    try {
      await axios.post(
        `${API_BASE_URL}/invitations/${id}/delete`,
        { force },
        { headers: { 'x-auth-token': token } }
      );
      setMessage('✓ Invitation deleted successfully');
      fetchInvitations();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to delete';
      setMessage(`✗ ${msg}`);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} invitations?`)) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/invitations/bulk-delete`,
        { invitationIds: selectedIds, force: false },
        { headers: { 'x-auth-token': token } }
      );
      setMessage(`✓ Deleted ${response.data.deletedCount} invitations`);
      setSelectedIds([]);
      fetchInvitations();
    } catch (error) {
      setMessage(`✗ ${error.response?.data?.message}`);
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/invitations/${id}/details`,
        { headers: { 'x-auth-token': token } }
      );
      setDetailsModalData(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      setMessage('✗ Failed to load details');
    }
  };

  const handleEditInvitation = async (id, updates) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/invitations/${id}/update`,
        updates,
        { headers: { 'x-auth-token': token } }
      );
      setMessage('✓ Invitation updated successfully');
      setShowEditModal(false);
      fetchInvitations();
    } catch (error) {
      setMessage(`✗ ${error.response?.data?.message}`);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);

      // If items are selected, export only those; otherwise use filters
      let queryString = '';
      if (selectedIds.length > 0) {
        // Export only selected invitations by ID
        queryString = selectedIds.map(id => `ids=${encodeURIComponent(id)}`).join('&');
      } else {
        // Export using current filters
        const filterQuery = buildFilterQuery();
        queryString = Object.entries(filterQuery)
          .filter(([_, value]) => value !== null && value !== '' && value !== undefined)
          .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
          .join('&');
      }

      const url = `${API_BASE_URL}/invitations/export${queryString ? '?' + queryString : ''}`;

      const response = await axios.get(url, {
        headers: { 'x-auth-token': token },
        responseType: 'blob'
      });

      // Create download link
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      const filename = selectedIds.length > 0
        ? `invitations_selected_${selectedIds.length}_${new Date().toISOString().split('T')[0]}.csv`
        : `invitations_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      const exportMessage = selectedIds.length > 0
        ? `✓ Exported ${selectedIds.length} selected invitation${selectedIds.length !== 1 ? 's' : ''}`
        : '✓ Export successful';
      setMessage(exportMessage);
    } catch (error) {
      console.error('Export error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Export failed';
      setMessage(`✗ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleFilter = (filterKey, value) => {
    setFilters(prev => {
      const currentValues = prev[filterKey] || [];
      if (currentValues.includes(value)) {
        return { ...prev, [filterKey]: currentValues.filter(v => v !== value) };
      } else {
        return { ...prev, [filterKey]: [...currentValues, value] };
      }
    });
  };

  const clearFilters = () => {
    setFilters({
      type: [],
      status: [],
      serviceGroupId: '',
      invitationGroupId: '',
      searchTerm: '',
      createdFrom: '',
      createdTo: '',
      expiresFrom: '',
      expiresTo: '',
      hasOnboardingFlow: false,
      autoActivate: null,
      usageStatus: ''
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === invitations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invitations.map(inv => inv._id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const getStatusBadgeStyle = (status) => {
    const styles = {
      active: { background: '#10b981', color: 'white' },
      used: { background: '#6b7280', color: 'white' },
      expired: { background: '#ef4444', color: 'white' },
      revoked: { background: '#dc2626', color: 'white' },
      inactive: { background: '#9ca3af', color: 'white' },
      depleted: { background: '#f59e0b', color: 'white' }
    };
    return styles[status] || {};
  };

  const typeIcons = {
    individual: '👤',
    bulk: '📋',
    general_link: '🔗'
  };

  const formatUsageStats = (invitation) => {
    if (invitation.invitationType !== 'general_link') return '-';
    if (!invitation.maxUses) return '∞';
    return `${invitation.usageCount || 0}/${invitation.maxUses}`;
  };

  // Group management handlers
  const handleCreateGroup = async (groupData) => {
    try {
      await axios.post(
        `${API_BASE_URL}/invitation-groups`,
        groupData,
        { headers: { 'x-auth-token': token } }
      );
      setMessage('✓ Group created successfully');
      setShowGroupModal(false);
      fetchInvitationGroups();
    } catch (error) {
      setMessage(`✗ ${error.response?.data?.message || 'Failed to create group'}`);
    }
  };

  const handleUpdateGroup = async (id, groupData) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/invitation-groups/${id}`,
        groupData,
        { headers: { 'x-auth-token': token } }
      );
      setMessage('✓ Group updated successfully');
      setShowGroupModal(false);
      fetchInvitationGroups();
    } catch (error) {
      setMessage(`✗ ${error.response?.data?.message || 'Failed to update group'}`);
    }
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm('Are you sure you want to delete this group? Invitations will remain but will no longer be grouped.')) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/invitation-groups/${id}`,
        { headers: { 'x-auth-token': token } }
      );
      setMessage('✓ Group deleted successfully');
      fetchInvitationGroups();
      fetchInvitations();
    } catch (error) {
      setMessage(`✗ ${error.response?.data?.message || 'Failed to delete group'}`);
    }
  };

  return (
    <div className="user-management-container">
      <h2>Client Invitation Management</h2>

      {message && (
        <div style={{
          padding: '10px',
          margin: '10px 0',
          backgroundColor: message.startsWith('✓') ? '#d4edda' : '#f8d7da',
          color: message.startsWith('✓') ? '#155724' : '#721c24',
          borderRadius: '4px'
        }}>
          {message}
        </div>
      )}

      <div style={{ borderBottom: '1px solid #ccc', marginBottom: '20px' }}>
        <button
          onClick={() => setActiveTab('individual')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'individual' ? '#007bff' : '#f0f0f0',
            color: activeTab === 'individual' ? '#fff' : '#000',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Individual Invitations
        </button>
        <button
          onClick={() => setActiveTab('bulk')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'bulk' ? '#007bff' : '#f0f0f0',
            color: activeTab === 'bulk' ? '#fff' : '#000',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Bulk CSV Upload
        </button>
        <button
          onClick={() => setActiveTab('general')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'general' ? '#007bff' : '#f0f0f0',
            color: activeTab === 'general' ? '#fff' : '#000',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          General Signup Links
        </button>
        <button
          onClick={() => setActiveTab('list')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'list' ? '#007bff' : '#f0f0f0',
            color: activeTab === 'list' ? '#fff' : '#000',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          View All Invitations
        </button>
        <button
          onClick={() => setActiveTab('groups')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'groups' ? '#007bff' : '#f0f0f0',
            color: activeTab === 'groups' ? '#fff' : '#000',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Invitation Groups
        </button>
      </div>

      {/* Individual Invitations Tab */}
      {activeTab === 'individual' && (
        <div>
          <h3>Create Individual Invitation</h3>
          <form onSubmit={handleIndividualSubmit} style={{ maxWidth: '500px' }}>
            <div style={{ marginBottom: '15px' }}>
              <label>Name:</label>
              <input
                type="text"
                value={individualForm.invitedName}
                onChange={(e) => setIndividualForm({ ...individualForm, invitedName: e.target.value })}
                required
                style={{ width: '100%', padding: '8px' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>Email:</label>
              <input
                type="email"
                value={individualForm.invitedEmail}
                onChange={(e) => setIndividualForm({ ...individualForm, invitedEmail: e.target.value })}
                required
                style={{ width: '100%', padding: '8px' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>Service Group:</label>
              <select
                value={individualForm.serviceGroupId}
                onChange={(e) => setIndividualForm({ ...individualForm, serviceGroupId: e.target.value })}
                required
                style={{ width: '100%', padding: '8px' }}
              >
                {serviceGroups.map(sg => (
                  <option key={sg._id} value={sg._id}>{sg.name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>Onboarding Flow (optional - uses default if not selected):</label>
              <select
                value={individualForm.onboardingFlowId}
                onChange={(e) => setIndividualForm({ ...individualForm, onboardingFlowId: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">-- Use Default --</option>
                {onboardingFlows.filter(f => f.isActive).map(flow => (
                  <option key={flow._id} value={flow._id}>{flow.name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>Assignment Rule (optional - uses default if not selected):</label>
              <select
                value={individualForm.assignmentRuleId}
                onChange={(e) => setIndividualForm({ ...individualForm, assignmentRuleId: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">-- Use Default --</option>
                {assignmentRules.filter(r => r.isActive).map(rule => (
                  <option key={rule._id} value={rule._id}>{rule.name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={individualForm.sendEmail}
                  onChange={(e) => setIndividualForm({ ...individualForm, sendEmail: e.target.checked })}
                />
                {' '}Send email immediately
              </label>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={individualForm.showServiceGroupLogo}
                  onChange={(e) => setIndividualForm({ ...individualForm, showServiceGroupLogo: e.target.checked })}
                />
                {' '}Show service group logo on signup page
              </label>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                (Logo must be configured in Service Groups settings)
              </div>
            </div>
            <button type="submit" disabled={loading} style={{ padding: '10px 20px', backgroundColor: '#28a745', color: '#fff', border: 'none', cursor: 'pointer' }}>
              {loading ? 'Creating...' : 'Create Invitation'}
            </button>
          </form>
        </div>
      )}

      {/* Bulk CSV Upload Tab */}
      {activeTab === 'bulk' && (
        <div>
          <h3>Bulk CSV Upload</h3>
          <p>Upload a CSV file with columns: name, email, serviceGroupId (optional), clinicalSupervisorId (optional), therapistId (optional)</p>
          <button onClick={downloadTemplate} style={{ marginBottom: '15px', padding: '8px 16px' }}>
            Download CSV Template
          </button>

          <div style={{ marginBottom: '15px' }}>
            <label>Default Service Group:</label>
            <select
              value={bulkServiceGroupId}
              onChange={(e) => setBulkServiceGroupId(e.target.value)}
              required
              style={{ width: '100%', padding: '8px', maxWidth: '400px' }}
            >
              {serviceGroups.map(sg => (
                <option key={sg._id} value={sg._id}>{sg.name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label>Onboarding Flow (optional - uses default if not selected):</label>
            <select
              value={bulkOnboardingFlowId}
              onChange={(e) => setBulkOnboardingFlowId(e.target.value)}
              style={{ width: '100%', padding: '8px', maxWidth: '400px' }}
            >
              <option value="">-- Use Default --</option>
              {onboardingFlows.filter(f => f.isActive).map(flow => (
                <option key={flow._id} value={flow._id}>{flow.name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label>Assignment Rule (optional - uses default if not selected):</label>
            <select
              value={bulkAssignmentRuleId}
              onChange={(e) => setBulkAssignmentRuleId(e.target.value)}
              style={{ width: '100%', padding: '8px', maxWidth: '400px' }}
            >
              <option value="">-- Use Default --</option>
              {assignmentRules.filter(r => r.isActive).map(rule => (
                <option key={rule._id} value={rule._id}>{rule.name}</option>
              ))}
            </select>
          </div>

          {/* Group Assignment Section */}
          <div style={{ background: '#f9f9f9', padding: '15px', marginBottom: '15px', borderRadius: '4px' }}>
            <h4>Invitation Group Assignment</h4>
            <div style={{ marginBottom: '10px' }}>
              <label>
                <input
                  type="radio"
                  value="none"
                  checked={bulkGroupOption === 'none'}
                  onChange={(e) => setBulkGroupOption(e.target.value)}
                />
                {' '}No Group
              </label>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label>
                <input
                  type="radio"
                  value="new"
                  checked={bulkGroupOption === 'new'}
                  onChange={(e) => setBulkGroupOption(e.target.value)}
                />
                {' '}Create New Group
              </label>
              {bulkGroupOption === 'new' && (
                <div style={{ marginLeft: '25px', marginTop: '10px' }}>
                  <input
                    type="text"
                    placeholder="Group Name (required)"
                    value={bulkNewGroupName}
                    onChange={(e) => setBulkNewGroupName(e.target.value)}
                    style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
                  />
                  <textarea
                    placeholder="Group Description (optional)"
                    value={bulkNewGroupDescription}
                    onChange={(e) => setBulkNewGroupDescription(e.target.value)}
                    rows={3}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
              )}
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label>
                <input
                  type="radio"
                  value="existing"
                  checked={bulkGroupOption === 'existing'}
                  onChange={(e) => setBulkGroupOption(e.target.value)}
                />
                {' '}Add to Existing Group
              </label>
              {bulkGroupOption === 'existing' && (
                <div style={{ marginLeft: '25px', marginTop: '10px' }}>
                  <select
                    value={bulkExistingGroupId}
                    onChange={(e) => setBulkExistingGroupId(e.target.value)}
                    style={{ width: '100%', padding: '8px' }}
                  >
                    <option value="">-- Select Group --</option>
                    {invitationGroups.map(group => (
                      <option key={group._id} value={group._id}>{group.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Email Control */}
          <div style={{ marginBottom: '15px', background: '#e8f4f8', padding: '15px', borderRadius: '4px' }}>
            <label style={{ fontSize: '16px', fontWeight: 'bold' }}>
              <input
                type="checkbox"
                checked={bulkSendEmails}
                onChange={(e) => setBulkSendEmails(e.target.checked)}
              />
              {' '}Send Email Invitations Automatically
            </label>
            <p style={{ margin: '5px 0 0 25px', fontSize: '14px', color: '#666' }}>
              Turn off to download CSV and send via your own system
            </p>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setCsvFile(e.target.files[0])}
              style={{ padding: '8px' }}
            />
          </div>

          <button onClick={handleCSVValidate} disabled={loading || !csvFile} style={{ marginRight: '10px', padding: '10px 20px', backgroundColor: '#007bff', color: '#fff', border: 'none', cursor: 'pointer' }}>
            Validate CSV
          </button>

          {bulkValidation && (
            <div style={{ marginTop: '20px' }}>
              <h4>Validation Results:</h4>
              <p>Total Rows: {bulkValidation.summary.total} | Valid: {bulkValidation.summary.valid} | Duplicates: {bulkValidation.summary.duplicate} | Errors: {bulkValidation.summary.error}</p>

              <button onClick={handleBulkCreate} disabled={loading || bulkValidation.summary.valid === 0} style={{ padding: '10px 20px', backgroundColor: '#28a745', color: '#fff', border: 'none', cursor: 'pointer' }}>
                {loading ? 'Creating...' : `Create ${bulkValidation.summary.valid} Invitations${bulkSendEmails ? ' & Send Emails' : ''}`}
              </button>

              {bulkValidation.duplicateRows?.length > 0 && (
                <details style={{ marginTop: '15px' }}>
                  <summary>Duplicate Rows ({bulkValidation.duplicateRows.length})</summary>
                  <ul>
                    {bulkValidation.duplicateRows.map((row, i) => (
                      <li key={i}>Row {row.rowNumber}: {row.email} - {row.reason}</li>
                    ))}
                  </ul>
                </details>
              )}

              {bulkValidation.errorRows?.length > 0 && (
                <details style={{ marginTop: '15px' }}>
                  <summary>Error Rows ({bulkValidation.errorRows.length})</summary>
                  <ul>
                    {bulkValidation.errorRows.map((row, i) => (
                      <li key={i}>Row {row.rowNumber}: {row.email} - {row.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {/* General Signup Links Tab */}
      {activeTab === 'general' && (
        <div>
          <h3>Create General Signup Link</h3>
          <form onSubmit={handleGeneralLinkSubmit} style={{ maxWidth: '500px' }}>
            <div style={{ marginBottom: '15px' }}>
              <label>Tag/Name (e.g., "UCL-Study"):</label>
              <input
                type="text"
                value={generalLinkForm.tag}
                onChange={(e) => setGeneralLinkForm({ ...generalLinkForm, tag: e.target.value })}
                required
                style={{ width: '100%', padding: '8px' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>Description:</label>
              <input
                type="text"
                value={generalLinkForm.description}
                onChange={(e) => setGeneralLinkForm({ ...generalLinkForm, description: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>Service Group:</label>
              <select
                value={generalLinkForm.serviceGroupId}
                onChange={(e) => setGeneralLinkForm({ ...generalLinkForm, serviceGroupId: e.target.value })}
                required
                style={{ width: '100%', padding: '8px' }}
              >
                {serviceGroups.map(sg => (
                  <option key={sg._id} value={sg._id}>{sg.name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>Onboarding Flow (optional - uses default if not selected):</label>
              <select
                value={generalLinkForm.onboardingFlowId}
                onChange={(e) => setGeneralLinkForm({ ...generalLinkForm, onboardingFlowId: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">-- Use Default --</option>
                {onboardingFlows.filter(f => f.isActive).map(flow => (
                  <option key={flow._id} value={flow._id}>{flow.name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>Assignment Rule (optional - uses default if not selected):</label>
              <select
                value={generalLinkForm.assignmentRuleId}
                onChange={(e) => setGeneralLinkForm({ ...generalLinkForm, assignmentRuleId: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">-- Use Default --</option>
                {assignmentRules.filter(r => r.isActive).map(rule => (
                  <option key={rule._id} value={rule._id}>{rule.name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>Max Uses (leave empty for unlimited):</label>
              <input
                type="number"
                min="1"
                value={generalLinkForm.maxUses}
                onChange={(e) => setGeneralLinkForm({ ...generalLinkForm, maxUses: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>Auto-Activate New Users:</label>
              <select
                value={generalLinkForm.autoActivate ? 'true' : 'false'}
                onChange={(e) => setGeneralLinkForm({ ...generalLinkForm, autoActivate: e.target.value === 'true' })}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="false">No - Users created as "Pending" (require manual activation)</option>
                <option value="true">Yes - Users activated immediately</option>
              </select>
              <small style={{ color: '#666', fontSize: '12px' }}>
                Choose "No" if you want to manually review and approve each signup before activation.
              </small>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>Welcome Message (optional):</label>
              <textarea
                value={generalLinkForm.welcomeMessage}
                onChange={(e) => setGeneralLinkForm({ ...generalLinkForm, welcomeMessage: e.target.value })}
                maxLength={500}
                rows={3}
                style={{ width: '100%', padding: '8px' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={generalLinkForm.showServiceGroupLogo}
                  onChange={(e) => setGeneralLinkForm({ ...generalLinkForm, showServiceGroupLogo: e.target.checked })}
                />
                {' '}Show service group logo on signup page
              </label>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                (Logo must be configured in Service Groups settings)
              </div>
            </div>
            <button type="submit" disabled={loading} style={{ padding: '10px 20px', backgroundColor: '#28a745', color: '#fff', border: 'none', cursor: 'pointer' }}>
              {loading ? 'Creating...' : 'Create General Link'}
            </button>
          </form>
        </div>
      )}

      {/* View All Invitations Tab */}
      {activeTab === 'list' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3>All Invitations</h3>
            <div>
              <button onClick={() => setShowFilters(!showFilters)} style={{ marginRight: '10px', padding: '8px 16px' }}>
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
              <button onClick={handleExport} disabled={loading} style={{ padding: '8px 16px' }}>
                Export to CSV
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div style={{ background: '#f9f9f9', padding: '15px', marginBottom: '15px', borderRadius: '4px' }}>
              <h4>Filters</h4>

              <div style={{ marginBottom: '10px' }}>
                <input
                  type="text"
                  placeholder="Search email, name, code, tag, notes..."
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '10px' }}>
                <div>
                  <label>Type:</label>
                  <div>
                    {['individual', 'bulk', 'general_link'].map(type => (
                      <label key={type} style={{ marginRight: '10px' }}>
                        <input
                          type="checkbox"
                          checked={filters.type.includes(type)}
                          onChange={() => toggleFilter('type', type)}
                        />
                        {' '}{type}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label>Status:</label>
                  <div>
                    {['active', 'used', 'expired', 'revoked', 'inactive', 'depleted'].map(status => (
                      <label key={status} style={{ marginRight: '10px' }}>
                        <input
                          type="checkbox"
                          checked={filters.status.includes(status)}
                          onChange={() => toggleFilter('status', status)}
                        />
                        {' '}{status}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label>Service Group:</label>
                  <select
                    value={filters.serviceGroupId}
                    onChange={(e) => setFilters({ ...filters, serviceGroupId: e.target.value })}
                    style={{ width: '100%', padding: '8px' }}
                  >
                    <option value="">All</option>
                    {serviceGroups.map(sg => (
                      <option key={sg._id} value={sg._id}>{sg.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label>Invitation Group:</label>
                  <select
                    value={filters.invitationGroupId}
                    onChange={(e) => setFilters({ ...filters, invitationGroupId: e.target.value })}
                    style={{ width: '100%', padding: '8px' }}
                  >
                    <option value="">All</option>
                    {invitationGroups.map(ig => (
                      <option key={ig._id} value={ig._id}>{ig.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginTop: '10px' }}>
                <button onClick={clearFilters} style={{ marginRight: '10px', padding: '8px 16px' }}>
                  Clear All Filters
                </button>
                <span style={{ color: '#666' }}>
                  Showing {pagination.total} invitation{pagination.total !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}

          {/* Bulk Actions Bar */}
          {selectedIds.length > 0 && (
            <div style={{ background: '#e3f2fd', padding: '10px', marginBottom: '15px', borderRadius: '4px' }}>
              <span>{selectedIds.length} selected</span>
              <button
                onClick={handleBulkDelete}
                style={{ marginLeft: '10px', padding: '6px 12px', background: '#dc3545', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '3px' }}
              >
                Delete Selected
              </button>
            </div>
          )}

          {/* Enhanced Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === invitations.length && invitations.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Type</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Email/Code</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Status</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Service Group</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Onboarding Flow</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Inv. Group</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Usage</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Auto-Activate</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Created</th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map(inv => (
                <tr key={inv._id}>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(inv._id)}
                      onChange={() => toggleSelect(inv._id)}
                    />
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {typeIcons[inv.invitationType]} {inv.invitationType}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {inv.invitedEmail || inv.signupCode}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    <span style={{ ...getStatusBadgeStyle(inv.status), padding: '3px 8px', borderRadius: '3px', fontSize: '12px' }}>
                      {inv.status}
                    </span>
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {inv.serviceGroupId ? (typeof inv.serviceGroupId === 'object' ? inv.serviceGroupId.name : serviceGroups.find(sg => sg._id === inv.serviceGroupId)?.name) || '-' : '-'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {inv.onboardingFlowId ? (typeof inv.onboardingFlowId === 'object' ? inv.onboardingFlowId.name : '-') : '-'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {inv.invitationGroupId ? (typeof inv.invitationGroupId === 'object' ? inv.invitationGroupId.name : invitationGroups.find(g => g._id === inv.invitationGroupId)?.name) || '-' : '-'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {formatUsageStats(inv)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {inv.autoActivate ? '✓' : '✗'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    {new Date(inv.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                    <button
                      onClick={() => handleViewDetails(inv._id)}
                      style={{ padding: '4px 8px', fontSize: '12px', marginRight: '5px' }}
                    >
                      View
                    </button>
                    {inv.invitationType === 'general_link' && (
                      <>
                        <button
                          onClick={() => { setEditModalData({ ...inv, tag: inv.generalLinkSettings?.tag || '', description: inv.generalLinkSettings?.description || '', welcomeMessage: inv.generalLinkSettings?.welcomeMessage || '' }); setShowEditModal(true); }}
                          style={{ padding: '4px 8px', fontSize: '12px', marginRight: '5px' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleShowQR(inv)}
                          style={{ padding: '4px 8px', fontSize: '12px', marginRight: '5px', background: '#6c757d', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '3px' }}
                        >
                          QR
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => {
                        const url = inv.invitationType === 'general_link'
                          ? `${window.location.origin}/login?code=${inv.signupCode}`
                          : `${window.location.origin}/login?invitation=${inv.invitationToken}`;
                        copyToClipboard(url);
                      }}
                      style={{ padding: '4px 8px', fontSize: '12px', marginRight: '5px' }}
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => handleDeleteInvitation(inv._id)}
                      style={{ padding: '4px 8px', fontSize: '12px', background: '#dc3545', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '3px' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span>Showing {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}</span>
            </div>
            <div>
              <button
                onClick={() => setPagination({ ...pagination, page: 1 })}
                disabled={pagination.page === 1}
                style={{ marginRight: '5px', padding: '6px 12px' }}
              >
                «
              </button>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
                style={{ marginRight: '5px', padding: '6px 12px' }}
              >
                ‹
              </button>
              <span style={{ margin: '0 10px' }}>Page {pagination.page} of {pagination.pages}</span>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page === pagination.pages}
                style={{ marginRight: '5px', padding: '6px 12px' }}
              >
                ›
              </button>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.pages })}
                disabled={pagination.page === pagination.pages}
                style={{ padding: '6px 12px' }}
              >
                »
              </button>
            </div>
            <div>
              <select
                value={pagination.limit}
                onChange={(e) => setPagination({ ...pagination, limit: parseInt(e.target.value), page: 1 })}
                style={{ padding: '6px' }}
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Invitation Groups Tab */}
      {activeTab === 'groups' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3>Invitation Groups</h3>
            <button
              onClick={() => {
                setGroupModalData({ name: '', description: '', isNew: true });
                setShowGroupModal(true);
              }}
              style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '4px' }}
            >
              Create New Group
            </button>
          </div>

          {invitationGroups.length === 0 ? (
            <p>No invitation groups yet. Create one to organize your invitations.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f0f0f0' }}>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Name</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Description</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Total</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Used</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Active</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Created</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitationGroups.map(group => (
                  <tr key={group._id}>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <strong>{group.name}</strong>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {group.description || '-'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {group.metadata?.totalInvitations || 0}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {group.metadata?.usedInvitations || 0}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {group.metadata?.activeInvitations || 0}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {new Date(group.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <button
                        onClick={() => {
                          setFilters({ ...filters, invitationGroupId: group._id });
                          setActiveTab('list');
                        }}
                        style={{ padding: '4px 8px', fontSize: '12px', marginRight: '5px' }}
                      >
                        View Invitations
                      </button>
                      <button
                        onClick={() => {
                          setGroupModalData({ ...group, isNew: false });
                          setShowGroupModal(true);
                        }}
                        style={{ padding: '4px 8px', fontSize: '12px', marginRight: '5px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteGroup(group._id)}
                        style={{ padding: '4px 8px', fontSize: '12px', background: '#dc3545', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '3px' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && detailsModalData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3>Invitation Details</h3>

            <div style={{ marginBottom: '15px' }}>
              <h4>Overview</h4>
              <p><strong>Type:</strong> {typeIcons[detailsModalData.invitation.invitationType]} {detailsModalData.invitation.invitationType}</p>
              <p><strong>Status:</strong> <span style={{ ...getStatusBadgeStyle(detailsModalData.invitation.status), padding: '3px 8px', borderRadius: '3px' }}>{detailsModalData.invitation.status}</span></p>
              <p><strong>Email/Code:</strong> {detailsModalData.invitation.invitedEmail || detailsModalData.invitation.signupCode}</p>
              {detailsModalData.invitation.invitationType === 'general_link' && (
                <>
                  <p><strong>Usage:</strong> {formatUsageStats(detailsModalData.invitation)}</p>
                  <p><strong>Auto-Activate:</strong> {detailsModalData.invitation.autoActivate ? 'Yes' : 'No'}</p>
                </>
              )}
              <p><strong>Created:</strong> {new Date(detailsModalData.invitation.createdAt).toLocaleString()}</p>
              {detailsModalData.invitation.expiresAt && (
                <p><strong>Expires:</strong> {new Date(detailsModalData.invitation.expiresAt).toLocaleString()}</p>
              )}
              {(detailsModalData.invitation.generalLinkSettings?.tag || detailsModalData.invitation.tag) && (
                <p><strong>Tag:</strong> {detailsModalData.invitation.generalLinkSettings?.tag || detailsModalData.invitation.tag}</p>
              )}
              {(detailsModalData.invitation.generalLinkSettings?.description || detailsModalData.invitation.description) && (
                <p><strong>Description:</strong> {detailsModalData.invitation.generalLinkSettings?.description || detailsModalData.invitation.description}</p>
              )}
              {detailsModalData.invitation.generalLinkSettings?.welcomeMessage && (
                <p><strong>Welcome Message:</strong> {detailsModalData.invitation.generalLinkSettings.welcomeMessage}</p>
              )}
              {detailsModalData.invitation.notes && (
                <p><strong>Notes:</strong> {detailsModalData.invitation.notes}</p>
              )}
            </div>

            {detailsModalData.onboardingFlow && (
              <div style={{ marginBottom: '15px' }}>
                <h4>Onboarding Flow</h4>
                <p><strong>Name:</strong> {detailsModalData.onboardingFlow.name}</p>
                <p><strong>Stages:</strong> {detailsModalData.onboardingFlow.stages?.length || 0}</p>
              </div>
            )}

            {detailsModalData.invitationGroup && (
              <div style={{ marginBottom: '15px' }}>
                <h4>Invitation Group</h4>
                <p><strong>Name:</strong> {detailsModalData.invitationGroup.name}</p>
                {detailsModalData.invitationGroup.description && (
                  <p><strong>Description:</strong> {detailsModalData.invitationGroup.description}</p>
                )}
              </div>
            )}

            {detailsModalData.stats && (
              <div style={{ marginBottom: '15px' }}>
                <h4>Statistics</h4>
                <p><strong>Usage Count:</strong> {detailsModalData.stats.usageCount || 0}</p>
                {detailsModalData.stats.lastUsedAt && (
                  <p><strong>Last Used:</strong> {new Date(detailsModalData.stats.lastUsedAt).toLocaleString()}</p>
                )}
              </div>
            )}

            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{ padding: '8px 16px', marginRight: '10px' }}
              >
                Close
              </button>
              {detailsModalData.invitation.invitationType === 'general_link' && (
                <button
                  onClick={() => {
                    const inv = detailsModalData.invitation;
                    setEditModalData({ ...inv, tag: inv.generalLinkSettings?.tag || '', description: inv.generalLinkSettings?.description || '', welcomeMessage: inv.generalLinkSettings?.welcomeMessage || '' });
                    setShowDetailsModal(false);
                    setShowEditModal(true);
                  }}
                  style={{ padding: '8px 16px', background: '#007bff', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '4px' }}
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editModalData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowEditModal(false)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                lineHeight: '1',
                padding: '0',
                width: '30px',
                height: '30px'
              }}
              aria-label="Close"
            >
              ×
            </button>
            <h3>Edit Invitation</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const expiresAtValue = formData.get('expiresAt');
              const updates = {
                serviceGroupId: formData.get('serviceGroupId'),
                maxUses: formData.get('maxUses') ? parseInt(formData.get('maxUses')) : null,
                // Only include expiresAt if it has a value, otherwise omit to preserve existing value
                ...(expiresAtValue ? { expiresAt: expiresAtValue } : {}),
                status: formData.get('status'),
                autoActivate: formData.get('autoActivate') === 'true',
                tag: formData.get('tag'),
                description: formData.get('description'),
                welcomeMessage: formData.get('welcomeMessage'),
                notes: formData.get('notes'),
                onboardingFlowId: formData.get('onboardingFlowId') || null,
                assignmentRuleId: formData.get('assignmentRuleId') || null,
                showServiceGroupLogo: formData.has('showServiceGroupLogo')
              };
              handleEditInvitation(editModalData._id, updates);
            }}>
              <div style={{ marginBottom: '15px' }}>
                <label>Service Group:</label>
                <select
                  name="serviceGroupId"
                  defaultValue={editModalData.serviceGroupId?._id || editModalData.serviceGroupId || ''}
                  style={{ width: '100%', padding: '8px' }}
                  required
                >
                  {serviceGroups.map(sg => (
                    <option key={sg._id} value={sg._id}>{sg.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Max Uses (leave empty for unlimited):</label>
                <input
                  type="number"
                  name="maxUses"
                  min={editModalData.usageCount || 0}
                  defaultValue={editModalData.maxUses || ''}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Expires At:</label>
                <input
                  type="datetime-local"
                  name="expiresAt"
                  defaultValue={editModalData.expiresAt ? (() => {
                    const d = new Date(editModalData.expiresAt);
                    // Format as local datetime (YYYY-MM-DDTHH:MM)
                    const year = d.getFullYear();
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    const hours = String(d.getHours()).padStart(2, '0');
                    const minutes = String(d.getMinutes()).padStart(2, '0');
                    return `${year}-${month}-${day}T${hours}:${minutes}`;
                  })() : ''}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Status:</label>
                <select
                  name="status"
                  defaultValue={editModalData.status}
                  style={{ width: '100%', padding: '8px' }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Auto-Activate:</label>
                <select
                  name="autoActivate"
                  defaultValue={editModalData.autoActivate ? 'true' : 'false'}
                  style={{ width: '100%', padding: '8px' }}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Onboarding Flow (optional - uses default if not selected):</label>
                <select
                  name="onboardingFlowId"
                  defaultValue={editModalData.onboardingFlowId?._id || editModalData.onboardingFlowId || ''}
                  style={{ width: '100%', padding: '8px' }}
                >
                  <option value="">-- Use Default --</option>
                  {onboardingFlows.filter(f => f.isActive).map(flow => (
                    <option key={flow._id} value={flow._id}>{flow.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Assignment Rule (optional - uses default if not selected):</label>
                <select
                  name="assignmentRuleId"
                  defaultValue={editModalData.assignmentRuleId?._id || editModalData.assignmentRuleId || ''}
                  style={{ width: '100%', padding: '8px' }}
                >
                  <option value="">-- Use Default --</option>
                  {assignmentRules.filter(r => r.isActive).map(rule => (
                    <option key={rule._id} value={rule._id}>{rule.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Tag:</label>
                <input
                  type="text"
                  name="tag"
                  value={editModalData.tag || ''}
                  onChange={(e) => setEditModalData(prev => ({...prev, tag: e.target.value}))}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Description:</label>
                <textarea
                  name="description"
                  value={editModalData.description || ''}
                  onChange={(e) => setEditModalData(prev => ({...prev, description: e.target.value}))}
                  maxLength={500}
                  rows={3}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Welcome Message:</label>
                <textarea
                  name="welcomeMessage"
                  value={editModalData.welcomeMessage || ''}
                  onChange={(e) => setEditModalData(prev => ({...prev, welcomeMessage: e.target.value}))}
                  maxLength={500}
                  rows={3}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Notes:</label>
                <textarea
                  name="notes"
                  value={editModalData.notes || ''}
                  onChange={(e) => setEditModalData(prev => ({...prev, notes: e.target.value}))}
                  maxLength={1000}
                  rows={3}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>
                  <input
                    type="checkbox"
                    name="showServiceGroupLogo"
                    defaultChecked={editModalData.displaySettings?.showServiceGroupLogo || false}
                  />
                  {' '}Show service group logo on signup page
                </label>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  (Logo must be configured in Service Groups settings)
                </div>
              </div>

              <div style={{ marginTop: '20px', textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={{ padding: '8px 16px', marginRight: '10px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '4px' }}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Group Modal */}
      {showGroupModal && groupModalData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3>{groupModalData.isNew ? 'Create New Group' : 'Edit Group'}</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const groupData = {
                name: formData.get('name'),
                description: formData.get('description'),
                notes: formData.get('notes')
              };
              if (groupModalData.isNew) {
                handleCreateGroup(groupData);
              } else {
                handleUpdateGroup(groupModalData._id, groupData);
              }
            }}>
              <div style={{ marginBottom: '15px' }}>
                <label>Name (required):</label>
                <input
                  type="text"
                  name="name"
                  defaultValue={groupModalData.name}
                  required
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Description:</label>
                <textarea
                  name="description"
                  defaultValue={groupModalData.description || ''}
                  rows={3}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>Notes (internal use):</label>
                <textarea
                  name="notes"
                  defaultValue={groupModalData.notes || ''}
                  rows={3}
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div style={{ marginTop: '20px', textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={() => setShowGroupModal(false)}
                  style={{ padding: '8px 16px', marginRight: '10px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '4px' }}
                >
                  {groupModalData.isNew ? 'Create Group' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && qrModalData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowQRModal(false)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                lineHeight: '1',
                padding: '0',
                width: '30px',
                height: '30px'
              }}
              aria-label="Close"
            >
              ×
            </button>
            <h3 style={{ marginBottom: '10px' }}>QR Code</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
              {qrModalData.tag}
            </p>

            <div ref={qrCodeRef} style={{ display: 'inline-block', padding: '20px', background: 'white', border: '1px solid #ddd', borderRadius: '8px' }}>
              <QRCodeSVG
                value={qrModalData.url}
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>

            <p style={{ fontSize: '12px', color: '#999', marginTop: '15px', wordBreak: 'break-all' }}>
              {qrModalData.url}
            </p>

            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => downloadQRCode('png')}
                style={{
                  padding: '10px 20px',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                Download PNG
              </button>
              <button
                onClick={() => downloadQRCode('svg')}
                style={{
                  padding: '10px 20px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                Download SVG
              </button>
            </div>

            <div style={{ marginTop: '15px' }}>
              <button
                onClick={() => copyToClipboard(qrModalData.url)}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                Copy URL
              </button>
            </div>

            <div style={{ marginTop: '20px' }}>
              <button
                onClick={() => setShowQRModal(false)}
                style={{ padding: '8px 16px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationManagement;
